import React, { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import DeleteModal from "../../components/models/deleteModal.jsx";
import {
  fetchContactQueries,
  deleteContactQuery,
} from "../../controllers/websiteController";
import DataTable, {
  createActionsColumn,
  DataTableFilters,
  DataTableSelect,
} from "../../components/data-table";

const INQUIRY_TYPES = [
  { value: "", label: "All" },
  { value: "current", label: "Current" },
  { value: "prospective", label: "Prospective" },
  { value: "new_project", label: "New Project" },
];

const text = (value) => value || "-";
const truncate = (str, n = 60) =>
  !str ? "-" : str.length > n ? str.slice(0, n - 1) + "…" : str;

function ContactRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // server-side pagination + filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedType, setAppliedType] = useState("");

  // selection + delete
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchContactQueries(
        page,
        limit,
        appliedSearch,
        appliedType,
      );
      setRows(res?.data || []);
      setTotal(res?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch contact queries:", err);
      setError("Failed to load contact queries.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedSearch, appliedType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTypeChange = (value) => {
    setAppliedType(value);
    setPage(1);
  };

  const handleConfirmDelete = async () => {
    if (!toDelete?.id) return;
    try {
      await deleteContactQuery(toDelete.id);
      setShowDelete(false);
      setToDelete(null);
      await load();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const columns = [
    { key: "name", label: "Name", render: text },
    { key: "businessEmail", label: "Email", render: text },
    { key: "businessName", label: "Business", render: text },
    { key: "businessPhone", label: "Phone", render: text },
    { key: "inquiryType", label: "Inquiry Type", render: text },
    {
      key: "message",
      label: "Message",
      render: (v) => <span title={v || ""}>{truncate(v, 60)}</span>,
    },
    {
      key: "createdAt",
      label: "Created",
      render: (v) => (v ? new Date(v).toLocaleString() : "-"),
    },
    createActionsColumn(
      (r) => [
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          onSelect: () => {
            setToDelete(r);
            setShowDelete(true);
          },
        },
      ],
      { getRowLabel: (r) => r.name || `query ${r.id}` },
    ),
  ];

  return (
    <div className="p-5 w-full max-w-full">
      <DataTable
        ariaLabel="Contact form queries"
        columns={columns}
        data={rows}
        loading={loading}
        loadingRowCount={8}
        error={error}
        searchable
        manualFiltering
        searchPlaceholder="Search name / email / company / phone / message…"
        onSearchChange={(value) => setAppliedSearch(value.trim())}
        filterable
        activeFilterCount={appliedType ? 1 : 0}
        filterContent={
          <DataTableFilters
            canClear={Boolean(appliedType)}
            onClear={() => handleTypeChange("")}
          >
            <DataTableSelect
              id="contact-inquiry-type"
              label="Inquiry Type"
              value={appliedType}
              onChange={handleTypeChange}
              options={INQUIRY_TYPES}
            />
          </DataTableFilters>
        }
        selectable
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        getRowAriaLabel={(r) => r.name || `query ${r.id}`}
        pagination
        page={page}
        pageSize={limit}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        labels={{ empty: "No records found" }}
      />

      <DeleteModal
        show={showDelete}
        handleClose={() => {
          setShowDelete(false);
          setToDelete(null);
        }}
        handleConfirm={handleConfirmDelete}
        message={
          toDelete
            ? `Are you sure you want to delete contact from "${toDelete.name}" (${toDelete.businessEmail})?`
            : "Are you sure you want to delete this item?"
        }
      />
    </div>
  );
}

export default ContactRequests;
